import { useState } from "react";
import type { CaseView, ChatSummary, PackView } from "../api";
import { api } from "../api";
import { Declaration } from "./Declaration";
import { ReviewSheet } from "./ReviewSheet";

/* ═══════════════════════════════════════════════════════════════════════
   What you are about to put your name on.

   The last screen before a business's own address goes live under our
   domain, and the first one in this product written for somebody deciding
   rather than answering. Everything above it in the builder asks; this
   only tells.

   ── Why it reads back rather than summarising ──────────────────────────
   Every line here is the chat itself — the services their customers will
   be offered in the business's own words, the questions crAIg will answer
   out loud, the work he will turn away. It comes out of `build()`, which
   is the function that runs at publish, so what they read is what they
   get. A prose summary written beside that would be a second description
   of the same thing, and the two would part company the first time
   either changed.

   ── The button does not put anything live ──────────────────────────────
   It sends a link to the address they gave, and the copy says so plainly.
   The temptation is to write "Publish" and have it publish, and the
   reason not to is that anybody can type anybody's email into a form: the
   click on that link is the only thing that makes the address theirs. So
   the button ends at the outbox, and it says outbox.

   ── Four states, one at a time ─────────────────────────────────────────
   What is missing · have a read · sign · send. `ReviewSheet` and
   `Declaration` are the customer intake's own components, unchanged,
   doing here exactly what they do there — which is the point: the gates a
   setup passes through are the gates every case passes through, and a
   second implementation of either is a second thing to keep honest.

   One at a time, never side by side. Putting the declaration next to the
   review sheet is how somebody signs without reading, and the reason the
   intake keeps them apart applies just as hard to a business about to put
   its own name on a public address.
   ═══════════════════════════════════════════════════════════════════════ */

export function PublishDrawer({
  kase,
  pack,
  summary,
  wide,
  open,
  onClose,
  onSigned,
}: {
  kase: CaseView;
  pack: PackView;
  summary: ChatSummary;
  /** Docked beside the builder, or slid over the top of it on a phone. */
  wide: boolean;
  open: boolean;
  onClose(): void;
  onSigned(c: CaseView): void;
}) {
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ready = summary.blocking.length === 0;

  async function publish() {
    setSending(true);
    setError(null);
    try {
      const r = await api.confirmSetup(kase.id);
      setSentTo(r.to);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <aside
      className={wide ? "sticky top-4 shrink-0" : "fixed inset-y-0 right-0 z-50 scroll-y"}
      style={
        wide
          ? { width: 320, display: open ? "block" : "none" }
          : {
              width: "min(92vw, 380px)",
              background: "var(--bg)",
              boxShadow: "var(--shadow-lg)",
              transform: open ? "translateX(0)" : "translateX(101%)",
              transition: "transform .28s cubic-bezier(.22,1,.36,1)",
              padding: "0.75rem",
              overflowY: "auto",
            }
      }
      aria-label="What this chat will do"
    >
      <div className="card overflow-hidden">
        <div
          className="flex items-center gap-2 px-4 py-3"
          style={{ borderBottom: "1px solid var(--line)" }}
        >
          <span style={{ fontWeight: 650, fontSize: 14 }}>Your chat</span>
          {!wide && (
            <button
              type="button"
              onClick={onClose}
              className="ml-auto btn btn-quiet btn-term"
              style={{ padding: "0.2rem 0.5rem" }}
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>

        <div className="px-4 py-4 space-y-4">
          {/* ── Identity ──────────────────────────────────────────────
              The two lines a business checks first and hardest: what it
              is called, and what its address will be. */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
              {summary.businessName ?? (
                <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>Still unnamed</span>
              )}
            </div>
            <div className="mono" style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 2 }}>
              {summary.address ?? "no address yet"}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.5 }}>
              Answering as <strong>{summary.assistantName ?? "crAIg"}</strong>
              {summary.template ? ` · started from ${summary.template}` : ""}
            </div>
          </div>

          <Group title="Handles" empty="Nothing chosen yet" items={summary.handles} />

          <Group
            title="Can answer"
            empty="Nothing yet — tell crAIg what your customers ask"
            items={summary.answers}
          />

          <Group
            title="Asks customers for"
            empty="No documents"
            items={summary.collects.map((d) => `${d.label}${d.required ? "" : " · optional"}`)}
          />

          {/* Its own group and its own wording. This is the only list here
              a computer enforces — everything else is crAIg using
              judgement, and a business should know which is which before
              a real customer is turned away by one. */}
          <Group
            title="Turns away"
            empty="Nobody — every enquiry comes through"
            items={summary.declines}
          />

          {summary.modules.length > 0 && (
            <div>
              <div className="term-label" style={{ marginBottom: 6, textTransform: "none" }}>
                Built in
              </div>
              <div className="flex flex-wrap gap-1.5">
                {summary.modules.map((m) => (
                  <span
                    key={m.id}
                    title={m.summary}
                    style={{
                      fontSize: 12,
                      padding: "0.2rem 0.5rem",
                      borderRadius: 999,
                      border: "1px solid var(--line)",
                      background: m.fromPack ? "transparent" : "var(--surface-2)",
                      color: "var(--ink-2)",
                    }}
                  >
                    {m.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Not errors, and styled so. These are things crAIg took a
              decision about and wants a second pair of eyes on — a photo
              he made required, a service area he could not turn into a
              rule. Burying them until publish is how a business finds out
              from a customer. */}
          {summary.notes.length > 0 && (
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.5,
                background: "var(--surface-2)",
                border: "1px solid var(--line)",
                borderRadius: "var(--radius)",
                padding: "0.6rem 0.7rem",
                color: "var(--ink-2)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Worth a look</div>
              <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                {summary.notes.map((n, i) => (
                  <li key={i} style={{ marginTop: i ? 4 : 0 }}>
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ── Publishing ─────────────────────────────────────────────────
          Three states, and they are three because they are three
          different things to say: what is still missing, the declaration
          itself, and where the email went. */}
      {sentTo ? (
        <div className="card mt-3 px-4 py-4">
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 6 }}>Check your email</div>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--ink-2)", margin: 0 }}>
            We've sent a link to <strong>{sentTo}</strong>. Click it and your chat is live at{" "}
            <span className="mono">{summary.address}</span>. Nothing happens until you do.
          </p>
          <button type="button" onClick={publish} disabled={sending} className="btn btn-quiet mt-3 w-full">
            {sending ? "Sending…" : "Send it again"}
          </button>
        </div>
      ) : !ready ? (
        <div className="card mt-3 px-4 py-4">
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 6 }}>
            {summary.blocking.length === 1
              ? "One thing left"
              : `${summary.blocking.length} things left`}
          </div>
          <ul
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--ink-2)",
              margin: 0,
              paddingLeft: "1.1rem",
            }}
          >
            {summary.blocking.map((b, i) => (
              <li key={i} style={{ marginTop: i ? 5 : 0 }}>
                {b}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 10, marginBottom: 0 }}>
            Fill these in on the left, or just tell crAIg — either works.
          </p>
        </div>
      ) : kase.needsReview ? (
        /* Everything is collected and nobody has checked it. The drawer
           above is a read-back of the configuration; this is the
           read-back of the answers, flagged items first. Both, because
           they are different things: what crAIg BUILT, and what crAIg
           HEARD. */
        <ReviewSheet
          kase={kase}
          pack={pack}
          onReviewed={onSigned}
          onCorrect={(ref) => {
            onClose();
            /* Let the drawer finish closing before jumping, or on a phone
               the row is still behind an overlay when the scroll runs. */
            requestAnimationFrame(() =>
              document
                .querySelector(`[data-field="${ref}"], [name="${ref}"]`)
                ?.scrollIntoView({ behavior: "smooth", block: "center" }),
            );
          }}
        />
      ) : !summary.signed ? (
        <Declaration kase={kase} pack={pack} onSigned={onSigned} />
      ) : (
        <div className="card mt-3 px-4 py-4">
          <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 6 }}>Ready to go</div>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--ink-2)", margin: 0 }}>
            Signed and checked. We'll email you a link — one click on that and{" "}
            <span className="mono">{summary.address}</span> is open to your customers.
          </p>
          {error && (
            <p style={{ fontSize: 12.5, color: "var(--danger)", marginTop: 8, marginBottom: 0 }}>
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={publish}
            disabled={sending}
            className="btn btn-primary mt-3 w-full"
          >
            {sending ? "Sending…" : "Publish"}
          </button>
        </div>
      )}
    </aside>
  );
}

/**
 * One read-back list.
 *
 * `empty` is a sentence rather than a hidden section, deliberately: "Turns
 * away — nobody" and "Turns away" missing from the page entirely are very
 * different pieces of information, and only one of them is true.
 */
function Group({ title, items, empty }: { title: string; items: string[]; empty: string }) {
  return (
    <div>
      <div className="term-label" style={{ marginBottom: 5, textTransform: "none" }}>
        {title}
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{empty}</div>
      ) : (
        <ul
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            color: "var(--ink-2)",
            margin: 0,
            paddingLeft: "1.1rem",
          }}
        >
          {items.map((t, i) => (
            <li key={i} style={{ marginTop: i ? 4 : 0 }}>
              {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
